import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, TextField, Typography, MenuItem, FormControlLabel, Switch, Alert } from '@mui/material';
import { Save, ArrowBack } from '@mui/icons-material';
import accountingService from '../../services/accounting.service';
import { useLanguage } from '../../i18n/LanguageContext';
import DimensionSelector, { DimensionSelectorRef } from '../../components/DimensionSelector';

const ACCOUNT_TYPES = ['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE'];

const AccountForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const isEdit = Boolean(id);
  const [error, setError] = useState('');
  const [accounts, setAccounts] = useState<any[]>([]);
  const dimRef = useRef<DimensionSelectorRef>(null);
  const [form, setForm] = useState({
    accountCode: '', accountName: '', accountType: 'ASSET',
    parentAccountId: '' as any, balance: 0, isActive: true, description: ''
  });

  useEffect(() => {
    loadAccounts();
    if (isEdit) loadAccount();
  }, []);

  const loadAccounts = async () => {
    try { const res = await accountingService.getAccounts(); setAccounts(res.data); }
    catch (err) { console.error(err); }
  };

  const loadAccount = async () => {
    try {
      const res = await accountingService.getAccount(Number(id));
      const a = res.data;
      setForm({
        accountCode: a.accountCode, accountName: a.accountName,
        accountType: a.accountType, parentAccountId: a.parentAccountId || '',
        balance: a.balance, isActive: a.isActive, description: a.description || ''
      });
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError('');
    try {
      const data = { ...form, parentAccountId: form.parentAccountId || null };
      let entityId = id ? Number(id) : 0;
      if (isEdit) {
        await accountingService.updateAccount(Number(id), data);
      } else {
        const res = await accountingService.createAccount(data);
        entityId = res.data.id;
      }

      // Save dimensions separately
      try {
        await dimRef.current?.save('ACCOUNT', entityId);
      } catch (dimErr: any) {
        console.error('Dimension save error:', dimErr.response?.status, dimErr.response?.data);
        const msg = dimErr.response?.data?.details || dimErr.response?.data?.message || dimErr.message || 'Error saving dimensions';
        setError(msg);
        return;
      }

      navigate('/accounts');
    } catch (err: any) {
      console.error('Account save error:', err.response?.status, err.response?.data);
      const msg = err.response?.data?.details || err.response?.data?.message || err.message || 'Error saving account';
      setError(msg);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{isEdit ? t('accounts.edit') : t('accounts.new')}</Typography>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/accounts')}>
          {t('common.back_to_list')}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField fullWidth required label={t('accounts.account_code')} value={form.accountCode}
          onChange={e => setForm({ ...form, accountCode: e.target.value })} />
        <TextField fullWidth required label={t('accounts.account_name')} value={form.accountName}
          onChange={e => setForm({ ...form, accountName: e.target.value })} />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField fullWidth select required label={t('accounts.account_type')} value={form.accountType}
          onChange={e => setForm({ ...form, accountType: e.target.value })}>
          {ACCOUNT_TYPES.map(at => <MenuItem key={at} value={at}>{at}</MenuItem>)}
        </TextField>
        <TextField fullWidth select label={t('accounts.parent_account')} value={form.parentAccountId}
          onChange={e => setForm({ ...form, parentAccountId: e.target.value })}>
          <MenuItem value="">{t('common.none')}</MenuItem>
          {accounts.filter(a => !id || a.id !== Number(id)).map((a: any) => (
            <MenuItem key={a.id} value={a.id}>{a.accountCode} - {a.accountName}</MenuItem>
          ))}
        </TextField>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField fullWidth type="number" label={t('accounts.balance')} value={form.balance}
          onChange={e => setForm({ ...form, balance: Number(e.target.value) })}
          inputProps={{ step: '0.01' }} />
        <TextField fullWidth label={t('common.description')} value={form.description}
          onChange={e => setForm({ ...form, description: e.target.value })} />
      </Box>

      <FormControlLabel control={
        <Switch checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} />
      } label={t('common.active')} />

      <DimensionSelector ref={dimRef} entityType="ACCOUNT" entityId={isEdit ? Number(id) : undefined} />

      <Box sx={{ mt: 3 }}>
        <Button type="submit" variant="contained" startIcon={<Save />} size="large">
          {isEdit ? t('common.update') : t('common.create')}
        </Button>
      </Box>
    </Box>
  );
};

export default AccountForm;
